import { useEffect, useState } from 'react'
import { apiFetch } from '../api/http'
import { BusinessMetricsError, fetchBusinessMetrics, type BusinessProgress } from '../businessMetricsRequest'
import { useRequestState } from '../useRequestState'
import { InsightChart } from '../charts/InsightChart'
import { formatCount, formatRange, formatSek, formatSekCompact, UNIT_SPACE } from '../format'
import { throughLabel, type Period } from '../landing'
import type { ChartDatum } from '../types'
import { InfoTip } from './InfoTip'
import { BusinessReconciliation, type Reconciliation } from './BusinessReconciliation'
import { SearchableSelect } from './SearchableSelect'
import './BusinessMetrics.css'

const API_BASE = import.meta.env.VITE_API_URL ?? ''

type Grain = 'day' | 'month' | 'year'
type Metrics = { revenue: number; orders: number; average_order_value: number | null }
/** MP-OS-200: stored figures say when they were calculated and the last day they cover; `stale` when the
 * live request failed and older stored figures were served instead. Live answers carry none of these. */
type Stamped = { as_of?: string; data_through?: string | null; stale?: boolean }
type Overview = Stamped & { rows: (Metrics & { name: string; period_kind?: string; period_from?: string; period_to?: string })[]; total: Metrics; note: string; synthetic?: boolean; source?: string
  reconciliation?: Reconciliation }
type Segments = Stamped & { rows: ChartDatum[]; series: string[]; totals: Record<string, number>; note: string }
type Result<T> = { key: string; data?: T; loadedAt?: number; error?: string; retryAt?: number; progress?: BusinessProgress }

export function businessWindow(period: Period, periods: Period[], grain: Grain) {
  const history = periods.filter(p => p.from <= period.from).sort((a, b) => b.from.localeCompare(a.from)).slice(0, 12)
  return { from: grain === 'day' ? period.from : (history.at(-1)?.from ?? period.from),
    to: period.through && period.through < period.to ? period.through : period.to }
}

type LastGood<T> = { selection: string; data: T; loadedAt: number }
/** A section's answer to the CURRENT request, and its last good answer for the same selection. */
type Section<T> = { current: Result<T> | null; lastGood: LastGood<T> | null }

/** MP-OS-200: `attempt` (Refresh, Retry) asks again for the same selection. Only the current
 * request's answer is current; the last good answer of the same range, grain and breakdown is
 * kept so a failed or pending refresh can show it, always marked outdated. A new `generation`
 * (a recalculation that discarded the stored figures) drops the last good answer. */
function useBusinessData<T extends Stamped>(query: string, breakdown: string, attempt: string, generation = 0): Section<T> {
  const base = `${query}&breakdown=${breakdown}`
  const key = `${base}&attempt=${attempt}`
  const selection = `${base}&generation=${generation}`
  const [result, setResult] = useRequestState<Result<T> | null>(key, null)
  const [lastGood, setLastGood] = useState<LastGood<T> | null>(null)
  useEffect(() => {
    const controller = new AbortController()
    // Segment revenue may answer 202 while it is computed; only the whole range is ever shown.
    fetchBusinessMetrics<T>(`${API_BASE}/api/dashboard/business-metrics?${base}`,
      controller.signal, progress => { if (!controller.signal.aborted) setResult({ key, progress }) })
      .then(body => {
        if (controller.signal.aborted) return
        const loadedAt = Date.now()
        setResult({ key, data: body, loadedAt })
        if (!body.stale) setLastGood({ selection, data: body, loadedAt })
      })
      .catch(error => {
        if (controller.signal.aborted) return
        const retryAfterS = error instanceof BusinessMetricsError ? error.retryAfterS : undefined
        setResult({ key, error: error instanceof Error ? error.message : 'Business data could not be loaded.',
          retryAt: retryAfterS ? Date.now() + retryAfterS * 1000 : undefined })
      })
    return () => controller.abort()
  }, [base, key, selection, setResult])
  return { current: result?.key === key ? result : null, lastGood: lastGood?.selection === selection ? lastGood : null }
}

/** What a section shows: the current answer, else the last good one marked outdated, else nothing.
 * MP-OS-200: stale stored figures (the live request failed) are outdated too; a last good answer of
 * this session is preferred to them. */
function shown<T extends Stamped>({ current, lastGood }: Section<T>): { data: T; loadedAt: number; outdated: boolean } | null {
  if (current?.data && !current.data.stale) return { data: current.data, loadedAt: current.loadedAt ?? Date.now(), outdated: false }
  if (lastGood) return { data: lastGood.data, loadedAt: lastGood.loadedAt, outdated: true }
  return current?.data ? { data: current.data, loadedAt: current.loadedAt ?? Date.now(), outdated: true } : null
}

const clock = (at: number) => new Date(at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
const stockholmClock = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Stockholm' })
const stockholmDay = (at: Date) => at.toLocaleDateString('en-CA', { timeZone: 'Europe/Stockholm' })
const stockholmShortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'Europe/Stockholm' })

/** The stamp of stored figures: the last day they cover (the top bar's date format) and when they were
 * calculated (Stockholm time; MPD-157: with the date when that was not today in Stockholm). Null for a
 * live answer, which says when it loaded instead. */
export function calculatedStamp({ as_of, data_through }: Stamped): string | null {
  if (!as_of && !data_through) return null
  const through = data_through ? `Data through ${throughLabel({ through: data_through } as Period).replace(/^through /, '')}` : null
  const day = as_of && stockholmDay(new Date(as_of)) !== stockholmDay(new Date()) ? `${stockholmShortDate(as_of)} ` : ''
  const calculated = as_of ? `calculated ${day}${stockholmClock(as_of)}` : null
  const text = [through, calculated].filter(Boolean).join(' · ')
  return text.charAt(0).toUpperCase() + text.slice(1)
}

/** MP-OS-200: every section says when its figures loaded, or for stored figures the data they cover
 * and when they were calculated; older data is visibly marked outdated. */
function LoadedStamp({ loadedAt, outdated, what, data }: { loadedAt: number; outdated: boolean; what: string; data: Stamped }) {
  const stamp = calculatedStamp(data)
  return outdated
    ? <p className="business-stamp business-outdated" data-testid="outdated-marker">Outdated · {stamp
      ? stamp.charAt(0).toLowerCase() + stamp.slice(1) : `loaded ${clock(loadedAt)}`}
      <InfoTip label={`About the outdated ${what}`} size="small">These are the last {what} that loaded. The latest
        request has not returned new figures, so they may have changed since. Retry or Refresh to load them again.</InfoTip></p>
    : <p className="business-stamp">{stamp ?? `Loaded ${clock(loadedAt)}`}</p>
}

/** Retry stays disabled while the server holds the failure (Retry-After), and says when to try again. */
function RetryButton({ retryAt, onRetry }: { retryAt?: number; onRetry: () => void }) {
  const remaining = () => Math.max(0, Math.ceil(((retryAt ?? 0) - Date.now()) / 1000))
  const [seconds, setSeconds] = useState(remaining)
  useEffect(() => {
    setSeconds(remaining())
    if (!retryAt) return
    const timer = setInterval(() => {
      const left = remaining()
      setSeconds(left)
      if (left === 0) clearInterval(timer)
    }, 1000)
    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryAt])
  return <>
    <button className="control-button" onClick={onRetry} disabled={seconds > 0}>Retry</button>
    {seconds > 0 && <span>Try again in {seconds} s.</span>}
  </>
}

/** MPD-86 P2: drops the range's stored segment periods; the view then polls the recomputation (202). */
async function recalculateSegments(query: string) {
  const r = await apiFetch(`${API_BASE}/api/dashboard/business-metrics/recalculate?${query}`, { method: 'POST' })
  if (!r.ok) {
    const detail = await r.json().then((body: { detail?: string }) => body.detail).catch(() => null)
    throw new Error(detail || 'Revenue by segment could not be recalculated.')
  }
}

// Cards and tables keep every digit; chart axes and tooltips use readable units ("60 M").
const fields = [
  { key: 'revenue', title: 'Revenue', unit: 'SEK', full: formatSek, tooltip: formatSekCompact,
    tip: 'Revenue is net of refunds.' },
  { key: 'orders', title: 'Distinct orders', unit: 'orders', full: formatCount, tooltip: formatCount, tip: null },
  { key: 'average_order_value', title: 'Average order value', unit: 'SEK/order', full: formatSek, tooltip: formatSekCompact,
    tip: 'Average order value uses Heart’s own metric for each period; it is not an average of daily averages. No order value is shown where the order count is zero.' },
] as const
const format = (value: number | null, full: (value: number) => string) => value === null ? 'Unavailable' : full(value)

export function BusinessMetrics({ period, periods, role }: { period: Period; periods: Period[]; role?: string }) {
  const [grain, setGrain] = useState<Grain>('month')
  const [refresh, setRefresh] = useState(0)
  const [overviewRetry, setOverviewRetry] = useState(0)
  const [segmentRetry, setSegmentRetry] = useState(0)
  const [recalculated, setRecalculated] = useState(0)
  const [recalculating, setRecalculating] = useState(false)
  const [recalculateError, setRecalculateError] = useState<string | null>(null)
  const [historyFrom, setHistoryFrom] = useState('')
  const range = businessWindow(period, periods, grain)
  if (grain !== 'day' && historyFrom >= range.from && historyFrom <= period.from) range.from = historyFrom
  const query = new URLSearchParams({ date_from: range.from, date_to: range.to, granularity: grain }).toString()
  const overviewSection = useBusinessData<Overview>(query, 'total', `${refresh}.${overviewRetry}`)
  const segmentSection = useBusinessData<Segments>(query, 'segment', `${refresh}.${segmentRetry}`, recalculated)
  const overview = overviewSection.current
  const segments = segmentSection.current
  const overviewShown = shown(overviewSection)
  const segmentsShown = shown(segmentSection)
  const recalculate = () => {
    setRecalculating(true)
    setRecalculateError(null)
    recalculateSegments(query)
      .then(() => { setRecalculated(n => n + 1); setSegmentRetry(n => n + 1) })
      .catch(error => setRecalculateError(error instanceof Error ? error.message : 'Revenue by segment could not be recalculated.'))
      .finally(() => setRecalculating(false))
  }
  return <section className="business-metrics" aria-label="Business metrics">
    <header className="business-heading">
      {/* MPD-64: explanations are ⓘ; the range line, errors and statuses stay visible. MPD-103: the
          staff-only note is in the heading ⓘ. Access is enforced by App's route guard and the API. */}
      <div><h1>Business metrics
        <InfoTip label="About business metrics">Revenue, orders and order value over time. Internal page: visible
          to MinPrilla staff only; suppliers cannot open it.</InfoTip></h1></div>
      <button className="control-button" onClick={() => setRefresh(n => n + 1)}>Refresh</button>
    </header>
    <div className="business-controls">
      <label>Interval <select className="control-select" value={grain} onChange={e => setGrain(e.target.value as Grain)}>
        <option value="day">Daily</option><option value="month">Monthly</option><option value="year">Yearly</option>
      </select></label>
      {/* MP-OS-189: up to 12 periods — the shared searchable picker. A <span>, not a <label>:
          a label forwards clicks on the picker's options to its button. */}
      {grain !== 'day' && <span className="business-control">Trend from <SearchableSelect label="Trend from" searchLabel="Search periods"
        showAllLabel="Show all periods" value={range.from} onChange={setHistoryFrom}
        options={periods.filter(p => p.from <= period.from && p.from >= businessWindow(period, periods, grain).from)
          .map(p => ({ value: p.from, label: p.label }))} /></span>}
      <label>Warehouse <select className="control-select" value="total" onChange={() => {}}>
        <option value="total">Total</option><option value="smd" disabled>SMD — pending</option>
        <option value="3pl" disabled>3PL — pending</option>
      </select></label>
      <InfoTip label="About the warehouse split" size="small">A split by warehouse (SMD / 3PL) is waiting for Heart to support it.</InfoTip>
    </div>
    <p className="business-source">{formatRange(range.from, range.to)} · {overviewShown?.data.source ?? 'Heart'} · SEK · All suppliers
      <InfoTip label="About the trend range" size="small">
        Monthly and yearly trends use the available history through the selected month. Partial periods are
        included; missing source observations are not treated as zero.
      </InfoTip></p>
    {/* MP-OS-200: a chart shows only the current request's data; older data is marked outdated and lighter. */}
    <div className="business-overview">
    {!overview && <p role="status">Loading business metrics…</p>}
    {overview?.error && <div className="business-retry"><p role="alert">{overview.error}</p>
      <RetryButton retryAt={overview.retryAt} onRetry={() => setOverviewRetry(n => n + 1)} /></div>}
    {overviewShown && <div className={overviewShown.outdated ? 'business-stale' : undefined}>
      <LoadedStamp loadedAt={overviewShown.loadedAt} outdated={overviewShown.outdated} what="revenue, orders and order value"
        data={overviewShown.data} />
      {overview?.data?.stale && <div className="business-retry">
        <RetryButton onRetry={() => setOverviewRetry(n => n + 1)} /></div>}
      <div className="business-cards">{fields.map(field => <article className="business-card" key={field.key}>
        <h2>{field.title}</h2>
        <p className="business-value">{format(overviewShown.data.total[field.key], field.full)}{UNIT_SPACE}<small>{field.unit}</small></p>
        <p className="business-source">Selected range total{field.key === 'average_order_value' ? ' · Source period average' : ''}
          {field.tip && <InfoTip label={`About the ${field.title.toLowerCase()} card`} size="small">{field.tip}</InfoTip>}</p>
        <div className="business-chart"><InsightChart animate={false} valueFormatter={field.tooltip}
          tooltipLabels={{ [field.unit]: field.title }} chart={{ type: 'line', unit: field.unit, ref: null, series: null,
          data: overviewShown.data.rows.map(row => ({ name: row.name, value: row[field.key],
            // MP-OS-207: the period fields let InsightChart mark a partial period (dashed segment,
            // hollow dot, caption, tooltip), so the name carries no ' (partial)' suffix.
            period_kind: row.period_kind ?? null, period_from: row.period_from ?? null, period_to: row.period_to ?? null })) }} /></div>
      </article>)}</div>
      <BusinessReconciliation total={overviewShown.data.total} reconciliation={overviewShown.data.reconciliation}
        from={range.from} to={range.to} partial={overviewShown.data.rows.some(row => row.period_kind === 'partial')} />
    </div>}
    </div>
    <article className="business-card business-segments">
      <div className="business-segments-heading"><h2>Revenue by segment</h2>
        {role === 'admin' && <>
          <button className="control-button" onClick={recalculate}
            disabled={recalculating || !(segments?.data || segments?.error)}>{recalculating ? 'Recalculating…' : 'Recalculate'}</button>
          <InfoTip label="About recalculating revenue by segment" size="small">
            Recalculates every period of this range from Heart. Stored periods are also checked again automatically after 7 days, or when Heart’s summaries change.
          </InfoTip></>}</div>
      {recalculateError && <p role="alert">{recalculateError}</p>}
      {!segments && <p role="status">Loading complete segment coverage…</p>}
      {/* MPD-115 #9: with the last loaded (outdated) chart on screen, say that chart is what is shown. */}
      {segments?.progress && segmentsShown && <p role="status">{segments.progress === 'slow' ? 'Still recalculating' : 'Recalculating'} the
        whole range; the chart below is the last loaded result.
        <InfoTip label="About recalculating the range" size="small">The new chart replaces it when every period of
          the range is verified. A long range can take a few minutes.</InfoTip></p>}
      {segments?.progress && !segmentsShown && <p role="status">{segments.progress === 'slow'
        ? 'Still calculating revenue by segment. The first load of a long range can take a few minutes.'
        : 'Calculating revenue by segment for every period in the range. The chart appears when the whole range is verified.'}</p>}
      {segments?.error && <div className="business-retry"><p role="alert">{segments.error}</p>
        <RetryButton retryAt={segments.retryAt} onRetry={() => setSegmentRetry(n => n + 1)} /></div>}
      {segmentsShown && <div className={segmentsShown.outdated ? 'business-stale' : undefined}>
        <LoadedStamp loadedAt={segmentsShown.loadedAt} outdated={segmentsShown.outdated} what="revenue by segment"
          data={segmentsShown.data} />
        {segments?.data?.stale && <div className="business-retry">
          <RetryButton onRetry={() => setSegmentRetry(n => n + 1)} /></div>}
        <div className="business-chart"><InsightChart animate={false} valueFormatter={formatSekCompact}
          chart={{ type: 'line', unit: 'SEK', ref: null,
          series: segmentsShown.data.series, data: segmentsShown.data.rows }} /></div>
        <table><caption>Revenue by segment · selected range
          <InfoTip label="About revenue by segment" size="small">{segmentsShown.data.note}</InfoTip></caption><thead><tr><th>Segment</th><th>SEK</th></tr></thead>
          <tbody>{segmentsShown.data.series.map(label => <tr key={label}><th scope="row">{label}</th><td>{formatSek(segmentsShown.data.totals[label])}</td></tr>)}</tbody>
        </table>
      </div>}
    </article>
  </section>
}
